import * as Constants from "../../config/constants/Constants.js";

/**
 * Get a potentially nested value from an object using dot notation.
 * E.g. getValue({ a: { b: { c: 1 } } }, "a.b.c") => 1
 */
export const getValue = (obj, key) => {
    const keys = key.split(".");
    return keys.reduce((obj, k) => (obj && obj[k] !== undefined ? obj[k] : undefined), obj);
};

/**
 * Set a potentially nested value to an object using dot notation.
 * E.g. setValue({ a: { b: { c: 1 } } }, "a.b.c", 2) => { a: { b: { c: 2 } } }
 */
export const setValue = (obj, key, value) => {
    const keys = key.split(".");
    const lastKey = keys.pop();

    const lastObj = keys.reduce((obj, k) => {
        obj[k] = obj[k] || {};
        return obj[k];
    }, obj);

    lastObj[lastKey] = value;
};

/**
 * Boolean whether an array or an object is empty.
 */
export const isEmpty = (obj) => {
    if (obj === undefined || obj === null) return true;

    if (isArray(obj)) return obj.length === 0;
    return Object.keys(obj).length === 0;
};

export const isObject = (obj) => {
    return typeof obj === "object" && !isArray(obj) && obj !== null;
};

export const isString = (str) => {
    return typeof str === "string";
};

export const isArray = (arr) => {
    return Array.isArray(arr);
};

/**
 * Set the state in a way that doesn't accidentally overwrite the previous state.
 */
export const setInsertObjectState = (setter, obj) => {
    console.warn(`Setting into state:`, obj);

    setter((prevState) => ({
        ...prevState,
        ...obj
    }));
};

/**
 * Set the state in a way that doesn't accidentally overwrite the previous state for arrays.
 * Uses the 'keys' parameter to remove duplicates.
 *
 * Example of a state: [{ id: 1, name: "John" }, { id: 2, name: "Jane" }]
 */
export const setInsertArrayState = (setter, data = [], options = {}) => {
    const { keys = ["id"], insertNewData = true } = options;

    console.warn(`Setting into state:`, data);

    setter((prev) => {
        let nextState = [...prev, ...data];

        // Only update data which was previously also in the state.
        if (!insertNewData) {
            const filteredNewData = data.filter((newData) => {
                return prev.some((oldData) => {
                    return keys.every((key) => oldData[key] === newData[key]);
                });
            });

            nextState = [...prev, ...filteredNewData];
        }

        return Array.from(new Map(nextState.map((el) => [keys.map((key) => el[key]).join("_"), el])).values());
    });
};

/**
 * Set the state in a way that removes data from the previous state for arrays.
 * Uses the 'keys' parameter to find objects to remove.
 *
 * Example: [{ id: 1, timestamp: 123, name: "John" }, { id: 2, timestamp: 987, name: "Jane" }],
 * where data = [1, 2] or [{ timestamp: 123, name: "John" }]
 */
export const setDeleteArrayState = (setter, data = [], options = {}) => {
    const { keys = ["id"] } = options;

    console.warn(`Removing from state using keys ${keys.join(", ")}:`, data);

    if (keys.length === 1) {
        setter((prev) => {
            return prev.filter((item) => {
                return !data.includes(getValue(item, keys[0]));
            });
        });
    } else {
        setter((prev) => {
            return prev.filter((item) => {
                return !data.some((dataItem) => {
                    return keys.every((key) => {
                        return getValue(item, key) === getValue(dataItem, key);
                    });
                });
            });
        });
    }
};

/**
 * Replace all placeholders in a string with values from an object, according to the key.
 * If there are any placeholders left, replace them with N/A.
 * In case of formatters, e.g. "...<key:format>...", replace with cmd(obj[key])
 */
export const replacePlaceholdersWithObjValues = (text, obj = {}) => {
    let newText = text;
    const formatterFunctions = { format_date_time: formatDate };

    for (const key in obj) {
        const regex = new RegExp(`<${key}(?::([^>]+))?>`, "g");
        newText = newText.replace(regex, (match, formatter) => {
            if (formatter && formatterFunctions[formatter]) {
                return formatterFunctions[formatter](obj[key]);
            }
            return obj[key];
        });
    }

    newText = newText.replace(/<[^>]*>/g, "N/A");

    return newText;
};

/**
 * Remove objects from the configuration by their key values.
 * All others will remain.
 * Supports wildcard patterns, e.g. "_*" will remove all keys starting with "_".
 *
 * Preserves the original data type.
 */
export const removeKeysFromObject = (obj, keysToRemove = []) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => removeKeysFromObject(item, keysToRemove));
    }

    if (typeof obj !== "object" || obj === null) {
        return obj;
    }

    const updatedObj = {};

    for (const key in obj) {
        if (!shouldRemoveKey(key, keysToRemove)) {
            updatedObj[key] = removeKeysFromObject(obj[key], keysToRemove);
        }
    }

    return updatedObj;
};

/**
 * Check if a key should be removed based on exact match or wildcard patterns.
 */
const shouldRemoveKey = (key, patterns) => {
    for (const pattern of patterns) {
        // Handle wildcard patterns
        if (pattern.includes("*")) {
            const prefix = pattern.replace("*", "");
            if (key.startsWith(prefix)) return true;
        }

        // Handle exact match
        else if (key === pattern) return true;
    }

    return false;
};

/**
 * Adds a 'form' object to every level of the configuration or a template.
 * Also includes a 'saved' key.
 *
 * E.g. used when creating new configuration rows, or add the 'form' keys back when applying a template as a configuration.
 */
export const addFormToObj = (obj, options = {}) => {
    const { formObj = Constants.FORM_ELEMENT, forceSaveTop = false, exclude = [], parentKey, isSaved = false, isTop = true } = options;

    if (Array.isArray(obj)) {
        return obj.map((item) => addFormToObj(item, { ...options, isTop: false }));
    }

    // Leave strings and other non-objects as-is.
    if (typeof obj === "object" && obj !== null) {
        const updatedObj = {};

        for (const key in obj) {
            updatedObj[key] = addFormToObj(obj[key], { ...options, parentKey: key, isTop: false });

            // Don't add the form element if the parent key is in the 'exclude' array.
            if (exclude.includes(parentKey)) continue;

            // Add a new key to the 'form' object.
            updatedObj.form = {
                ...updatedObj.form,
                [key]: { ...formObj }
            };
        }

        // Add the 'saved' key as well to non-empty objects.
        if (!exclude.includes(parentKey)) {
            if (updatedObj.form) updatedObj.form.saved = isSaved;
        }

        // Force add the 'saved' key to certain objects.
        if (forceSaveTop && isTop) {
            if (updatedObj.form) updatedObj.form.saved = true;
        }

        return updatedObj;
    }

    return obj;
};

/**
 * Include objects from the configuration by their key values.
 * All others will be removed.
 *
 * Preserves the original data type.
 */
export const includeKeysFromObject = (obj, keysToInclude = []) => {
    if (keysToInclude.includes("*")) return obj;
    if (Array.isArray(obj)) return obj.map((item) => includeKeysFromObject(item, keysToInclude));
    if (typeof obj !== "object") return obj;

    const updatedObj = { ...obj };

    for (const key in updatedObj) {
        if (!matchesWildcard(key, keysToInclude) && !keysToInclude.includes(key)) {
            delete updatedObj[key];
        } else if (typeof updatedObj[key] === "object" && updatedObj[key] !== null) {
            updatedObj[key] = includeKeysFromObject(updatedObj[key], keysToInclude);
        }
    }

    return updatedObj;
};

/**
 * Check if a string matches any wildcard ("*") patterns in the provided array.
 */
const matchesWildcard = (str, patterns) => {
    for (const pattern of patterns) {
        if (wildcardMatch(str, pattern)) {
            return true;
        }
    }

    return false;
};

/**
 * Check if a string matches a wildcard ("*") pattern.
 */
const wildcardMatch = (str, pattern) => {
    const regex = new RegExp(`^${String(pattern).replace(/\*/g, ".*")}$`);
    return regex.test(String(str));
};

export const getElementIndex = (arr, element) => {
    return arr.findIndex((el) => el === element);
};

export const isElementFirst = (arr, element) => {
    const currentIndex = getElementIndex(arr, element);
    return currentIndex === 0;
};

export const isElementLast = (arr, element) => {
    const currentIndex = getElementIndex(arr, element);
    const configLength = arr.length;
    return currentIndex === configLength - 1;
};

/**
 * Returns whether an object exists in an array based on a specified key.
 */
export const isObjExistsInArray = (arr = [], obj = {}, key = "id") => {
    return arr.some((item) => item[key] === obj[key]);
};

/**
 * Return the parent key of a nested key.
 */
export const findParentKey = (obj, targetKey, parentKey = null) => {
    for (const key in obj) {
        if (obj[key] === targetKey) {
            return parentKey;
        }

        if (typeof obj[key] === "object") {
            const result = findParentKey(obj[key], targetKey, key);

            if (result !== null) {
                return result;
            }
        }
    }

    return null;
};

/**
 * Convert seconds to formatted time: HH:MM:SS
 * E.g. 65 -> 00:01:05, 3600 -> 01:00:00
 */
export const convertSecondsToFormattedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);

    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

/**
 * Return a Date object.
 *
 * Parsing:
 * - 2022-12-11T17:06:15.475445+02:00 (ISO 8601 format)
 * - 20230113T173000 (slicing format)
 * - 1677649423 (Unix timestamp in seconds)
 */
export const convertToDate = (date) => {
    let convertedDate;

    if (typeof date === "string") {
        if (date.includes("-")) {
            convertedDate = new Date(Date.parse(date));
        } else if (date.includes("T")) {
            convertedDate = new Date(date.slice(0, 4), date.slice(4, 6) - 1, date.slice(6, 8), date.slice(9, 11), date.slice(11, 13), date.slice(13, 15));
        } else if (/^\d{15}$/.test(date)) {
            convertedDate = new Date(date.slice(0, 4), date.slice(4, 6) - 1, date.slice(6, 8), date.slice(9, 11), date.slice(11, 13), date.slice(13, 15));
        } else if (/^\d{13}$/.test(date)) {
            convertedDate = new Date(parseInt(date));
        }
    }

    if (typeof date === "number") {
        convertedDate = new Date(date * 1000);
    }

    return convertedDate;
};

/**
 * Return date as a formatted locale string.
 */
export const formatDate = (date, options = {}) => {
    const { onlyDate = false } = options;
    let formattedDate = "N/A";

    if (!(date instanceof Date)) {
        date = convertToDate(date);
    }

    if (isValidDate(date)) {
        if (onlyDate) {
            formattedDate = date.toLocaleDateString();
        } else {
            formattedDate = date.toLocaleString();
        }
    }

    return formattedDate;
};

/**
 * Boolean whether possible to parse date.
 */
const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date);
};

/**
 * Capitalize the first letter of a string.
 */
export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Capitalize the first letter of a string, and replace underscores with spaces.
 * This is useful for formatting keys or constants into human-readable text.
 *
 * E.g. LEVEL_1_REVIEWER -> Level 1 reviewer
 */
export const formatText = (string) => {
    if (!string) return "N/A";
    return capitalizeFirstLetter(string?.toLowerCase()?.replaceAll("_", " ")) ?? "N/A";
};

/**
 * Truncate a string to a specified length and append an ellipsis if it exceeds that length.
 */
export const getEllipsis = (text, length) => {
    if (!text || typeof text !== "string") return "";
    return text.length > length ? text.slice(0, length) + "..." : text;
};

/**
 * Return null (None in python) if dealing with an empty string.
 */
export const nullIfEmptyString = (value) => {
    if (value === "" || value === undefined) return null;
    return value;
};

/**
 * Return null (None in python) if dealing with a non-number.
 */
export const nullIfNotNumber = (value) => {
    if (isNaN(value)) return null;
    return value;
};

/**
 * Return null (None in python) if dealing with either an empty string or a non-number.
 */
export const nullIfEmptyStringOrNotNumber = (value) => {
    const number = nullIfNotNumber(value);
    const string = nullIfEmptyString(value);

    if (number === null || string === null) return null;
    return value;
};

/**
 * Remove all keys from an object if the value of said key is null.
 * Return the object.
 */
export const removeNullValues = (obj = {}) => {
    for (const key in obj) {
        if (obj[key] === null) {
            delete obj[key];
        }
    }

    return obj;
};

/**
 * Remove all keys from an object if the value of said key is an empty string.
 * Return the object.
 */
export const removeEmptyStrings = (obj = {}, nullable = []) => {
    for (const key in obj) {
        if (nullable.includes(key)) continue;

        if (obj[key] === "") {
            delete obj[key];
        }
    }

    return obj;
};

/**
 * Compare two nested objects that may contain objects and arrays.
 */
export const areObjectsSimilar = (obj1 = {}, obj2 = {}) => {
    if (obj1 === null && obj2 === null) return true;
    if (obj1 === null || obj2 === null) return false;

    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
        return obj1 === obj2;
    }

    const keys1 = Object.keys(obj1).filter((key) => !key.includes("form"));
    const keys2 = Object.keys(obj2).filter((key) => !key.includes("form"));

    if (keys1.length !== keys2.length) return false;

    // Check if the values for each key are the same.
    for (let key of keys1) {
        const value1 = obj1[key];
        const value2 = obj2[key];

        if (typeof value1 === "object" && typeof value2 === "object") {
            // Recursive call for nested objects.
            if (!areObjectsSimilar(value1, value2)) {
                return false;
            }
        } else if (Array.isArray(value1) && Array.isArray(value2)) {
            // Check if arrays are equal.
            if (!areArraysEqual(value1, value2)) {
                return false;
            }
        } else if (value1 !== value2) {
            // Check if values are equal.
            return false;
        }
    }

    return true;
};

/**
 * Combine two objects by iterating through the keys of obj1 and updating the corresponding keys in obj2.
 */
export const combineObjects = (obj1 = {}, obj2 = {}) => {
    const result = JSON.parse(JSON.stringify(obj2));

    for (const key in obj1) {
        if (obj1.hasOwnProperty(key) && typeof obj1[key] !== "undefined") {
            result[key] = obj1[key];
        }
    }

    return result;
};

/**
 * Returns an object containing only the keys whose values have changed between obj1 and obj2.
 */
export const getModifiedValues = (obj1 = {}, obj2 = {}) => {
    const modifiedValues = {};

    for (const key in obj1) {
        if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
            if (obj1[key] !== obj2[key]) {
                modifiedValues[key] = obj2[key];
            }
        } else if (obj1.hasOwnProperty(key)) {
            modifiedValues[key] = obj1[key];
        }
    }

    for (const key in obj2) {
        if (!obj1.hasOwnProperty(key)) {
            modifiedValues[key] = obj2[key];
        }
    }

    return modifiedValues;
};

/**
 * Compare two arrays.
 */
export const areArraysEqual = (arr1 = [], arr2 = []) => {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
        if (!areObjectsSimilar(arr1[i], arr2[i])) {
            return false;
        }
    }

    return true;
};

/**
 * Sorts an array of objects based on a specified key in either ascending or descending order.
 */
export const sortArrayByKey = (array = [], key = "key", order = "asc") => {
    const sortedArray = JSON.parse(JSON.stringify(array));

    sortedArray.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (order === "asc") {
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
        } else if (order === "desc") {
            if (aValue > bValue) return -1;
            if (aValue < bValue) return 1;
        }

        return 0;
    });

    return sortedArray;
};

/**
 * Find and return the missing elements from one array in another array.
 */
export const findMissingArrayElements = (requiredArray, fullArray) => {
    const fullSet = new Set(fullArray);

    // Use filter() to find elements in requiredArray that are not in set2
    return requiredArray.filter((element) => !fullSet.has(element));
};

/**
 * Return an array where duplicates have been removed.
 */
export const getUniqueValues = (arr = []) => {
    const uniqueSet = new Set(arr);
    return Array.from(uniqueSet);
};

/**
 * Disable logging to the console.
 */
export const disableConsoleLogging = () => {
    if (!window.console) window.console = {};

    const methods = ["log", "debug", "info"];

    for (let i = 0; i < methods.length; i++) {
        console[methods[i]] = function () {};
    }
};

/**
 * Pretty-print the object.
 */
export const formatObj = (obj = {}) => {
    return JSON.stringify(obj, null, 4);
};

/**
 * Create the HTTP request structure that will be sent to the API.
 *
 * Remove any form items.
 * Only include those keys that are specified in the 'include' field.
 * Remove empty strings.
 *
 * Return a deep copy of the object.
 */
export const createUploadObject = (obj, exclude = ["form"], include = ["*"], nullable = []) => {
    const uploadObj = JSON.parse(JSON.stringify(obj));

    const excludedKeys = removeKeysFromObject(uploadObj, exclude);
    const includedKeys = includeKeysFromObject(excludedKeys, include);

    return removeEmptyStrings(includedKeys, nullable);
};

/**
 * Return whether a string is a YouTube URL.
 */
export const isUser = (props) => {
    return props?.user?.me?.role?.role === Constants.ROLE_USER;
};

export const isAdmin = (props) => {
    return props?.user?.me?.role?.role === Constants.ROLE_ADMIN;
};
